/**
 * Maps an optical scan onto the client form.
 *
 * Deliberately **not** the chip mapper with a different input. The two reads are not the same kind
 * of evidence and must not behave the same way on a form:
 *
 * - The chip is an authenticated record from the issuing authority, and it supplies a whole person:
 *   name, national number, document, issuer, both dates, and an address. Replacing the form wholesale
 *   is right there, and blanking what the card does not carry is what stops a second read leaving the
 *   previous customer's details behind.
 * - An optical scan is a photograph of a machine-readable zone. It supplies five of those fields and
 *   has no opinion whatsoever about the rest. A mapper that blanked the others would erase an issuer
 *   and an address the operator had already typed, on the strength of a document that never claimed
 *   anything about them.
 *
 * So this one proposes rather than replaces: it fills what is empty, leaves what it cannot speak
 * for, and hands back everything that disagrees for a person to settle. Nothing here writes to a
 * form; the caller applies what comes out.
 */

import { buildFullName, splitDocumentNumber, type ClientFormValues } from './idCardClientForm';
import type { OpticalScanResult, OpticalValidationState } from './opticalScan';

/** The client-form fields a machine-readable zone can speak for. Deliberately a short list. */
export type OpticalFormField = 'cnp' | 'nume' | 'serieCI' | 'nrCI' | 'dataExpirareCI';

/** What happened to one field. */
export type OpticalFieldOutcome =
    /** The form was empty here and the scan filled it. */
    | 'filled'
    /** The form already said the same thing. */
    | 'unchanged'
    /** The form says something else. Left alone; a person decides. */
    | 'conflict'
    /** The scan read nothing for this field. */
    | 'unavailable'
    /** The scan read something the document's own check digit refuses. Never offered. */
    | 'rejected';

/** One field, what the scan made of it, and what the form already had. */
export interface OpticalFieldProposal {
    readonly field: OpticalFormField;
    readonly scanned: string;
    readonly current: string;
    readonly outcome: OpticalFieldOutcome;
}

/** Everything an optical scan has to say about a client form. */
export interface OpticalFormProposal {
    /**
     * The values that can be applied without asking anyone: fields the scan read and the form had
     * nothing in. Never contains a conflict, and never contains a blank for a field the scan could
     * not read.
     */
    readonly values: Partial<ClientFormValues>;
    readonly proposals: readonly OpticalFieldProposal[];
    /** The subset a person has to settle. Empty on the ordinary path. */
    readonly conflicts: readonly OpticalFieldProposal[];
    /**
     * Whether the document validated as a whole.
     *
     * Not a gate — a scan of a genuine document can fail a check digit because of a smudge, and
     * refusing to show what was read would leave an operator with nothing. It is what a screen warns
     * on before anything is applied.
     */
    readonly trustworthy: boolean;
}

/**
 * Works out what an optical scan would do to a form, without doing it.
 *
 * @param result The completed scan.
 * @param current What the form holds now. Missing keys count as empty.
 */
export function proposeOpticalClientForm(
    result: OpticalScanResult,
    current: Partial<ClientFormValues>,
): OpticalFormProposal {
    const document = result.document;
    const validation = result.validation;
    const { series, number } = splitDocumentNumber(document.documentNumber?.value);

    // A check digit that failed is the document itself saying the value was misread. Writing one of
    // those onto a contract is worse than writing nothing, so those fields are never offered - not
    // even as a conflict for somebody to accept.
    const documentNumberRejected = checkDigitFailed(result, 'document-number');

    const candidates: readonly [OpticalFormField, string, boolean][] = [
        ['cnp', document.personalNumber?.value ?? '', validation.personalNumber === 'invalid'],
        ['nume', buildFullName(document.surname?.value, document.givenNames?.value), false],
        ['serieCI', series, documentNumberRejected],
        ['nrCI', number, documentNumberRejected],
        [
            'dataExpirareCI',
            document.expiryDate?.value ?? '',
            checkDigitFailed(result, 'expiry-date'),
        ],
    ];

    const proposals = candidates.map(([field, scanned, rejected]) =>
        judge(field, scanned, current[field] ?? '', rejected),
    );

    const values: Partial<ClientFormValues> = {};

    for (const proposal of proposals) {
        if (proposal.outcome === 'filled') {
            values[proposal.field] = proposal.scanned;
        }
    }

    return {
        values,
        proposals,
        conflicts: proposals.filter(proposal => proposal.outcome === 'conflict'),
        trustworthy: validation.overall === 'valid',
    };
}

/**
 * Applies the conflicts a person accepted.
 *
 * Separate from {@link proposeOpticalClientForm} so that overwriting somebody's typing is always an
 * act with a decision behind it, never a side effect of scanning.
 */
export function applyOpticalConflicts(
    proposal: OpticalFormProposal,
    accepted: readonly OpticalFormField[],
): Partial<ClientFormValues> {
    const values: Partial<ClientFormValues> = { ...proposal.values };

    for (const conflict of proposal.conflicts) {
        if (accepted.includes(conflict.field)) {
            values[conflict.field] = conflict.scanned;
        }
    }

    return values;
}

/** Whether a named check digit came back wrong. */
function checkDigitFailed(
    result: OpticalScanResult,
    field: 'document-number' | 'expiry-date',
): boolean {
    return result.validation.checkDigits.some(
        digit => digit.field === field && digit.state === 'invalid',
    );
}

/** Decides what one field's fate is. */
function judge(
    field: OpticalFormField,
    scanned: string,
    current: string,
    rejected: boolean,
): OpticalFieldProposal {
    const value = scanned.trim();

    if (value.length === 0) {
        return { field, scanned: '', current, outcome: 'unavailable' };
    }

    if (rejected) {
        return { field, scanned: value, current, outcome: 'rejected' };
    }

    if (current.trim().length === 0) {
        return { field, scanned: value, current, outcome: 'filled' };
    }

    return {
        field,
        scanned: value,
        current,
        outcome: same(value, current) ? 'unchanged' : 'conflict',
    };
}

/**
 * Whether two values are the same thing written differently.
 *
 * Diacritics are folded away on purpose. A machine-readable zone cannot carry them - `ȘTEFAN` is
 * written `STEFAN` there by the standard - so comparing them literally would raise a conflict on
 * every Romanian name with a diacritic in it and train operators to click through the one screen
 * that exists to catch a real disagreement.
 */
function same(left: string, right: string): boolean {
    return fold(left) === fold(right);
}

function fold(value: string): string {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^0-9A-Za-z]/g, '')
        .toUpperCase();
}

/** Whether a validation state should be shown as a warning rather than a failure. */
export function isValidationInconclusive(state: OpticalValidationState): boolean {
    return state === 'unavailable' || state === 'not-evaluated';
}
